/**
 * The managed-product-page entry point: a link into Plan that lands on the
 * scan modal's ready state for that product.
 */
export function planFromInventoryPath(managedProduct) {
  const params = new URLSearchParams({
    managedProductId: String(managedProduct.id),
    managedProductName: managedProduct.name
  });
  return `/plan?${params.toString()}`;
}

export function readInventoryDeepLink(searchParams) {
  const raw = searchParams.get('managedProductId');
  if (!raw) return null;
  const id = Number(raw);
  if (!Number.isInteger(id)) return null;
  return { id, name: searchParams.get('managedProductName') || '' };
}

/**
 * The ServiceNow CI-record link: /plan?vendor=f5&product=BIG-IP+LTM&version=…&ci=…
 *
 * Distinct from readInventoryDeepLink, which carries a managedProductId from
 * our own managed-product page. Here the caller knows one device's vendor,
 * product and version but nothing about the tenant's managed products, so the
 * portal has to resolve that itself.
 *
 * Partial parameters are kept rather than rejected — someone arriving from a
 * CI record came to do something, so we pre-fill whatever we were given.
 * `ci` is carried but unused today; it is accepted now so the link format
 * does not have to change when a later story scopes a scan to one CI.
 */
export function readVendorProductDeepLink(searchParams) {
  const vendorId = searchParams.get('vendor');
  if (!vendorId) return null;
  return {
    vendorId,
    productName: searchParams.get('product') || null,
    version: searchParams.get('version') || null,
    ciSysId: searchParams.get('ci') || null
  };
}

const normalizeName = value =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

export function findManagedProductFor(managedProducts, productName) {
  const wanted = normalizeName(productName);
  if (!wanted) return null;
  const match = (managedProducts || []).find(
    mp => !mp.isDisabled && normalizeName(mp.name) === wanted
  );
  return match || null;
}

/**
 * Resolve a ServiceNow deep-link product name against the vendor's product
 * options (`{ value: productId, label: productName }`, built by
 * fetchProducts). The link only ever carries a display name, so match it
 * against either field rather than assuming which one the vendor's catalog
 * uses for both: F5 happens to return productId === productName for all of
 * its products today (verified against the live tenant 2026-08-12), so a
 * value-only match currently "works" for F5 by coincidence. That must not be
 * relied on — any vendor whose ids and names differ would otherwise silently
 * break this prefill fallback, which exists specifically so a user arriving
 * from a CI record for an untracked product isn't dead-ended.
 */
export function findProductOption(productOptions, productName) {
  if (!productName) return null;
  return (
    (productOptions || []).find(
      p => p.value === productName || p.label === productName
    ) || null
  );
}

/**
 * What the wizard should do on arrival from a ServiceNow CI record.
 *
 * Lives here rather than inline in the wizard so the rule is testable
 * without rendering a 3200-line component: scan when the tenant tracks the
 * product, otherwise pre-fill the manual form rather than dead-ending
 * someone who arrived with clear intent.
 */
export function resolveDeepLinkAction(searchParams, managedProducts) {
  const link = readVendorProductDeepLink(searchParams);
  if (!link) return { action: 'none' };
  const managedProduct = findManagedProductFor(managedProducts, link.productName);
  if (managedProduct) {
    return {
      action: 'scan',
      managedProductId: managedProduct.id,
      managedProductName: managedProduct.name,
      link
    };
  }
  return { action: 'prefill', link };
}
